import axios from '../config/axiosConfig';

const API_URL = '/notifications';

// Get notifications for current user
const getNotifications = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.unreadOnly) params.append('unreadOnly', 'true');
  if (options.type) params.append('type', options.type);
  if (options.limit) params.append('limit', options.limit);
  if (options.skip) params.append('skip', options.skip);

  const response = await axios.get(`${API_URL}?${params.toString()}`);
  return response.data;
};

// Get unread count
const getUnreadCount = async () => {
  const response = await axios.get(`${API_URL}/unread-count`);
  return response.data.count;
};

// Mark notifications as read
const markAsRead = async (notificationIds = null) => {
  const response = await axios.put(`${API_URL}/read`, { notificationIds });
  return response.data;
};

// Mark single notification as read
const markOneAsRead = async (notificationId) => {
  const response = await axios.put(`${API_URL}/${notificationId}/read`);
  return response.data;
};

// Dismiss notifications
const dismiss = async (notificationIds = null) => {
  const response = await axios.delete(`${API_URL}/dismiss`, { data: { notificationIds } });
  return response.data;
};

// Dismiss single notification
const dismissOne = async (notificationId) => {
  const response = await axios.delete(`${API_URL}/${notificationId}`);
  return response.data;
};

// Get notification preferences
const getPreferences = async () => {
  const response = await axios.get(`${API_URL}/preferences`);
  return response.data;
};

// Update notification preferences
const updatePreferences = async (preferences) => {
  const response = await axios.put(`${API_URL}/preferences`, { preferences });
  return response.data;
};

// Send test notification
const sendTest = async () => {
  const response = await axios.post(`${API_URL}/test`);
  return response.data;
};

// Get notification statistics
const getStats = async () => {
  const response = await axios.get(`${API_URL}/stats`);
  return response.data;
};

// Notification type info
const notificationTypes = {
  // Comment notifications
  comment_mention: { label: 'Mention', icon: 'at', color: 'blue' },
  comment_reply: { label: 'Reply', icon: 'reply', color: 'blue' },
  comment_reaction: { label: 'Reaction', icon: 'heart', color: 'pink' },

  // Action notifications
  action_assigned: { label: 'Assigned', icon: 'user-plus', color: 'purple' },
  action_status_changed: { label: 'Status Changed', icon: 'sync', color: 'yellow' },
  action_completed: { label: 'Completed', icon: 'check-circle', color: 'green' },

  // Finding notifications
  finding_created: { label: 'New Finding', icon: 'exclamation-triangle', color: 'orange' },
  finding_correlated: { label: 'Correlated', icon: 'link', color: 'teal' },
  finding_status_changed: { label: 'Status Changed', icon: 'sync', color: 'yellow' },

  // Project notifications
  project_invite: { label: 'Invitation', icon: 'envelope', color: 'blue' },
  project_role_changed: { label: 'Role Changed', icon: 'user-shield', color: 'purple' },
  project_completed: { label: 'Completed', icon: 'flag-checkered', color: 'green' },

  // Agent notifications
  agent_connected: { label: 'Agent Connected', icon: 'plug', color: 'green' },
  agent_disconnected: { label: 'Agent Disconnected', icon: 'unlink', color: 'red' },
  agent_task_completed: { label: 'Task Completed', icon: 'tasks', color: 'green' },
  agent_task_failed: { label: 'Task Failed', icon: 'times-circle', color: 'red' },

  // Playbook notifications
  playbook_started: { label: 'Playbook Started', icon: 'play', color: 'blue' },
  playbook_completed: { label: 'Playbook Completed', icon: 'check-double', color: 'green' },
  playbook_failed: { label: 'Playbook Failed', icon: 'exclamation-circle', color: 'red' },

  // System notifications
  system_alert: { label: 'System Alert', icon: 'bell', color: 'yellow' },
  system_update: { label: 'Update', icon: 'download', color: 'blue' },

  // SIEM notifications
  siem_alert: { label: 'SIEM Alert', icon: 'shield-alt', color: 'orange' },
  siem_sync_failed: { label: 'Sync Failed', icon: 'sync-alt', color: 'red' },
  siem_correlation_complete: { label: 'SIEM Check Complete', icon: 'search-plus', color: 'teal' },
  siem_correlation_failed: { label: 'SIEM Check Failed', icon: 'exclamation-circle', color: 'red' },

  // Team notifications
  team_member_added: { label: 'Member Added', icon: 'user-plus', color: 'green' },
  team_member_removed: { label: 'Member Removed', icon: 'user-minus', color: 'red' }
};

// Priority labels and colors
const priorityInfo = {
  low: { label: 'Low', color: 'gray' },
  normal: { label: 'Normal', color: 'blue' },
  high: { label: 'High', color: 'orange' },
  urgent: { label: 'Urgent', color: 'red' }
};

// Get notification type info
const getTypeInfo = (type) => {
  return notificationTypes[type] || { label: type, icon: 'bell', color: 'gray' };
};

// Get priority info
const getPriorityInfo = (priority) => {
  return priorityInfo[priority] || priorityInfo.normal;
};

// Format notification time
const formatTime = (date) => {
  const now = new Date();
  const notifDate = new Date(date);
  const diffMs = now - notifDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return notifDate.toLocaleDateString();
};

const notificationService = {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markOneAsRead,
  dismiss,
  dismissOne,
  getPreferences,
  updatePreferences,
  sendTest,
  getStats,
  notificationTypes,
  priorityInfo,
  getTypeInfo,
  getPriorityInfo,
  formatTime
};

export default notificationService;
